import { Component, DestroyRef, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import * as _ from 'lodash';

import { ScreenContentMode } from '../../core/enums/content-mode.enum';
import { BehaviorScreen } from '../../core/models/view-models/monitor-models/screen.type';
import { ScheduleSettingsDialogService } from '../../core/services/dialog-services/schedule-dialog.service';
import { Schedule } from '../../core/models/entities/schedule.type';
import { ScheduleSettingsMode } from '../../core/enums/screen-settings-mode.enum';
import { ScheduleModalMode } from '../../core/enums/schedule-model-mode.enum';
import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { PlaylistStateService } from '../../core/services/screen-state/playlists-state.service';
import { UIService } from '../../core/services/utils/ui.service';
import { PlaylistDTO } from '../../core/models/view-models/playlist-models/playlist-dto.model';
import { PlaylistsService } from '../../core/services/http-services/playlists.service';
import { MonitorsService } from '../../core/services/http-services/monitors.service';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { PluralAssignment } from '../../core/models/view-models/monitor-models/monitor-assignment.type';
import { PlayerService } from '../../core/services/http-services/player.service';
import { ShareContentInfo } from '../../core/interfaces/share-content-info.interface';

@Component({
  selector: 'bottom-menu',
  templateUrl: './bottom-menu.component.html',
  styleUrls: ['./bottom-menu.component.scss']
})
export class BottomMenuComponent implements OnDestroy {
  contentModes = ScreenContentMode;
  subscriptions: Subscription = new Subscription();

  selectedPlaylists: PlaylistDTO[] = [];
  playlists: PlaylistDTO[] = [];

  screens: BehaviorScreen[] = [];
  selectedScreens: BehaviorScreen[] = [];

  configMonitorsMultiselect = {
    displayKey: 'MonitorTitle',
    search: false,
    upward: true,
    limitTo: 5,
    placeholder: 'Select monitor(s)'
  };

  configPlaylistsMultiselect = {
    displayKey: 'PlaylistTitle',
    search: false,
    upward: true,
    placeholder: 'Select playlist'
  };
  isPlayNowClicked: boolean = false;

  allPlaylists: PlaylistDTO[] = [];

  isBottomPanelPinned: boolean;
  isBottomPanelVisible: boolean;

  constructor(
    public scheduleModalService: ScheduleSettingsDialogService,
    public playlistsStateService: PlaylistStateService,
    public screenStateService: ScreensStateService,
    public uiService: UIService,
    private playlistDataService: PlaylistsService,
    private monitorsService: MonitorsService,
    private playerService: PlayerService,
    private destroyRef: DestroyRef
  ) {
    this.subscriptions.add(
      this.screenStateService.playlistSelectionChanged.subscribe(data => {
        this.selectedPlaylists = data && this.playlists.length ? [data] : [];
      })
    );

    this.subscriptions.add(
      this.playlistDataService.playlistCollectionChanged.subscribe(data => {
        this.allPlaylists = data;
        this.playlists = data;
        this.playlists = PlaylistStateService.filterPlaylistsByScreens(
          data,
          this.selectedScreens
        );
      })
    );

    this.subscriptions.add(
      this.screenStateService.selectedScreensChanged.subscribe(data => {
        this.selectedScreens = data;
        this.playlists = PlaylistStateService.filterPlaylistsByScreens(
          this.allPlaylists,
          data
        );
      })
    );

    this.subscriptions.add(
      this.screenStateService.monitorsInTheDashboard.subscribe(data => {
        if (data.every(screen => screen instanceof BehaviorScreen)) {
          this.screens = [ ...(data ? data : []) ];
        }
      })
    );

    this.subscriptions.add(
      this.uiService.bottomPanelPinnedSubject.subscribe(data => {
        this.isBottomPanelPinned = data;
      })
    );

    this.subscriptions.add(
      this.uiService.bottomPanelVisibleSubject.subscribe(data => {
        this.isBottomPanelVisible = data;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  // event handlers
  onStartPlaylist() {
    if (
      this.selectedScreens &&
      this.selectedScreens.length &&
      this.selectedPlaylists &&
      this.selectedPlaylists.length
    ) {
      this.selectedScreens.forEach(x => {
        x.playPlaylist(this.selectedPlaylists[0]);
      });
    }
    this.isPlayNowClicked = true;
  }

  onMouseLeave(event: MouseEvent): void {
    if (this.isPlayNowClicked) {
      this.uiService.hideBottomPanel();
    }
    this.isPlayNowClicked = false;
  }

  onNewSchedule() {
    if (
      this.selectedScreens.length &&
      this.selectedPlaylists &&
      this.selectedPlaylists.length
    ) {
      const screens = this.selectedScreens.map(
        ({ MachinePhysicalAddress, MonitorHandler }) => ({
          MachinePhysicalAddress: MachinePhysicalAddress,
          MonitorHandler
        })
      );

      const schedule = new Schedule();
      schedule.PlaylistId = this.selectedPlaylists[0].Id;
      schedule.ScheduleType = ScheduleSettingsMode.Daily;
      schedule.Monitors = screens;

      this.scheduleModalService.show(schedule, ScheduleModalMode.Create);
    }
  }

  onStopPlaylist() {
    if (this.selectedScreens && this.selectedScreens.length) {
      this.selectedScreens.forEach(screen => {
        screen.stopPlaylist();
      });
    }
  }

  onMonitorsMultiselectChange(event) {
    const value: BehaviorScreen[] = event.value;
    this.screenStateService.changeScreens(value);
  }

  onPlaylistSelectChanged(selectedPlaylists: PlaylistDTO[]) {
    const playlist = selectedPlaylists[0] || null;
    this.playlistsStateService.changePlaylist(playlist);

    if (!playlist) {
      this.selectedScreens.forEach(screen => screen.dismissPlaylist());
    }
  }

  onCancelUploading() {
    this.selectedScreens.forEach(screen => screen.dismissPlaylist(true));
  }

  onStartSharing(shareContent: ShareContentInfo) {
    this.uiService.shareContentLink.set(null);

    const androidScreens = this.screenStateService._selectedScreens.filter(screen => screen.Machine.IsAndroid);
    const windowsScreens = this.screenStateService._selectedScreens.filter(screen => !screen.Machine.IsAndroid);

    const playlists = [
      ...(androidScreens.length > 0 ? [this.playlistsStateService.createUrlPlaylist(shareContent, true)] : []),
      ...(windowsScreens.length > 0 ? [this.playlistsStateService.createUrlPlaylist(shareContent, false)] : [])
    ];

    playlists.forEach(playlist => {
      const relatedScreens = playlist.IsAndroid ? androidScreens : windowsScreens;
      const monitorKeys = relatedScreens.map(screen => new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress));
      this.screenStateService.setPendingState(relatedScreens);

      this.playlistDataService.createPlaylist(playlist).pipe(
        switchMap(createdPlaylist => this.monitorsService.assignPlaylistToPlural(new PluralAssignment(monitorKeys, createdPlaylist.Id))),
        switchMap(() => this.playerService.startAssignedPlaylistOnAllMonitors(monitorKeys, playlist.IsAndroid)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }
}
